// LP 그래프 기록을 '사용자 현지 날짜 하루당 점 하나'로 정리한다.
//
// 백엔드 tier-chart 의 timestamp 는 실제 조회 시각이 아니라 '한국 날짜의 UTC 자정' 으로 만든 날짜 키다.
// 그래서 한국 날짜가 먼저 바뀌는 시간대(한국 00~09시, 유럽 16~24시 등)에는 마지막 기록이 미래 시각이 되고,
// 이걸 시각으로 그대로 쓰면 툴팁에 내일 날짜가 찍히거나, 오늘 기록이 없다고 판단해 현재 점이 하나 더 붙었다.
//
// 처리 순서
// 1. 기록마다 관측 시각을 정한다. observed_at(티어를 실제로 확인한 시각)이 있으면 그 값을, 없으면 timestamp 를 쓰고,
//    현재보다 미래면 현재 시각으로 맞춘다.
// 2. 관측 시각을 현지 날짜로 바꿔, 같은 날짜에 기록이 여러 개면 관측 시각이 가장 늦은 기록 하나만 남긴다.
// 3. current(현재 티어)가 있으면 오늘 날짜의 값으로 쓴다. 랭크 박스와 그래프 마지막 점이 항상 같게 된다.
// 4. 빈 날짜는 전날 값으로 채우고, 최근 maxDays 일만 남긴다.
// 5. 각 점의 timestamp 는 현지 날짜의 정오로 맞춘다. 축 라벨·툴팁 '오늘/N일 전'·툴팁 날짜가 모두 같은 날짜를 가리킨다.

const RANKED_TIERS = ['IRON', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'EMERALD', 'DIAMOND', 'MASTER', 'GRANDMASTER', 'CHALLENGER'];

const toLocalNoon = (ms) => {
    const date = new Date(ms);
    date.setHours(12, 0, 0, 0);
    return date.getTime();
};

const addDays = (noonMs, days) => {
    const date = new Date(noonMs);
    date.setDate(date.getDate() + days);
    date.setHours(12, 0, 0, 0);
    return date.getTime();
};

export const normalizeLPChartRecords = (records, {current, now = Date.now(), maxDays = 31} = {}) => {
    const byDay = new Map(); // 현지 날짜 정오(ms) -> { observedMs, record }

    const put = (observedMs, record) => {
        const day = toLocalNoon(observedMs);
        const prev = byDay.get(day);
        if (!prev || observedMs >= prev.observedMs) {
            byDay.set(day, {observedMs, record});
        }
    };

    (Array.isArray(records) ? records : []).forEach((record) => {
        if (!record || typeof record.timestamp !== 'number') return;
        const baseSec = typeof record.observed_at === 'number' ? record.observed_at : record.timestamp;
        put(Math.min(baseSec * 1000, now), record);
    });

    if (current && RANKED_TIERS.includes(String(current.tier).toUpperCase())) {
        const today = byDay.get(toLocalNoon(now));
        byDay.set(toLocalNoon(now), {
            observedMs: now,
            record: {
                ...(today ? today.record : {}),
                tier: current.tier,
                division: current.division,
                league_points: current.league_points,
            },
        });
    }

    if (byDay.size === 0) return [];

    const days = [...byDay.keys()].sort((a, b) => a - b);
    const lastDay = days[days.length - 1];
    const windowStart = addDays(lastDay, -(maxDays - 1));

    // 표시 구간 시작 전의 마지막 기록을 이어받아 첫 날짜의 빈 값을 채운다.
    let carry = null;
    days.forEach((day) => {
        if (day < windowStart) carry = byDay.get(day).record;
    });

    const result = [];
    for (let day = Math.max(days[0], windowStart); day <= lastDay; day = addDays(day, 1)) {
        if (byDay.has(day)) carry = byDay.get(day).record;
        result.push({...carry, timestamp: day / 1000});
    }
    return result;
};
